import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  FormControl,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Fade,
  Dialog,
  DialogContent,
  Zoom,
  InputLabel,
} from '@mui/material';
import {
  Wifi,
  Groups,
  Devices,
  CastForEducation,
  CheckCircle,
  ArrowForward,
  Psychology,
} from '@mui/icons-material';
import {
  getCourseById,
  getTurmas,
  registerUserCourse,
} from '../services/api.js';
import { getCurrentUser } from '../utils/auth.js';

const InscricaoCursoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [curso, setCurso] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedModalidade, setSelectedModalidade] = useState(''); // Stores sg_modalidade ('R' or 'H')
  const [selectedVersionId, setSelectedVersionId] = useState(''); // Stores id_curso_versao
  const [turmas, setTurmas] = useState([]);
  const [selectedTurma, setSelectedTurma] = useState('');
  const [loadingTurmas, setLoadingTurmas] = useState(false);

  const [registering, setRegistering] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const data = await getCourseById(id);
        setCurso(data);
      } catch {
        setError('Erro ao carregar os detalhes do curso.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCourse();
    }
  }, [id]);

  useEffect(() => {
    if (curso) {
      if (curso.curso_modalidade && !selectedVersionId) {
        // TEMPORARY: Forcing 'R' (Remote) as Hybrid is disabled
        const modString = 'R';

        const version = curso.versoes.find(v => v.sg_modalidade === modString);
        if (version) {
          selectModalidade(version);
        } else if (curso.versoes?.length > 0) {
          const fallback = curso.versoes.find(v => v.sg_modalidade !== 'H');
          if (fallback) selectModalidade(fallback);
        }
      } else if (curso.versoes?.length > 0 && !selectedVersionId) {
        const fallback = curso.versoes.find(v => v.sg_modalidade !== 'H');
        if (fallback) selectModalidade(fallback);
      }
    }
  }, [curso, selectedVersionId]);

  useEffect(() => {
    if (curso && selectedModalidade) {
      const fetchTurmas = async () => {
        try {
          setLoadingTurmas(true);
          const data = await getTurmas(curso.id_curso, selectedModalidade);

          // Robust client-side filtering fallback
          const filteredData = Array.isArray(data)
            ? data.filter(
                t =>
                  t.sg_modalidade === selectedModalidade ||
                  t.modalidade === selectedModalidade ||
                  t.id_curso_versao === selectedVersionId
              )
            : [];

          setTurmas(filteredData);
        } catch {
          setTurmas([]);
        } finally {
          setLoadingTurmas(false);
        }
      };
      fetchTurmas();
    } else {
      setTurmas([]);
      if (selectedModalidade !== 'H') {
        setSelectedTurma('');
      }
    }
  }, [selectedModalidade, curso, selectedVersionId]);

  const selectModalidade = mod => {
    if (!mod) return;
    setSelectedModalidade(mod.sg_modalidade);
    setSelectedVersionId(mod.id_curso_versao);
  };

  const handleRegister = async () => {
    if (!selectedVersionId) return;
    if (
      (selectedModalidade === 'H' || selectedModalidade === 'R') &&
      !selectedTurma
    ) {
      setError('Selecione uma turma para a inscrição.');
      return;
    }

    const user = getCurrentUser();
    if (!user) {
      setError('Usuário não autenticado.');
      return;
    }

    try {
      setRegistering(true);
      setError(null);

      const payload = {
        id_curso: curso.id_curso,
        id_curso_versao: selectedVersionId,
        id_usuario: user.id_usuario,
        ...(selectedModalidade === 'H' ||
          (selectedModalidade === 'R' && { id_turma: selectedTurma })),
      };

      await registerUserCourse(payload);
      setSuccess(true);
      window.setTimeout(() => {
        navigate('/cursos');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Erro ao realizar inscrição.');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: '#FAFAFA',
        }}
      >
        <CircularProgress sx={{ color: '#4A90E2' }} />
      </Box>
    );
  }

  if (!curso) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity='error'>Curso não encontrado.</Alert>
      </Container>
    );
  }

  const remoteVersion = curso.versoes?.find(v => v.sg_modalidade === 'R');
  // TEMPORARY: Hybrid modality disabled because it is not fully ready
  // const hybridVersion = curso.versoes?.find(v => v.sg_modalidade === 'H');
  const hybridVersion = null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FAFAFA',
      }}
    >
      <Container maxWidth='lg' sx={{ py: 8, flexGrow: 1 }}>
        {/* Success Modal */}
        <Dialog
          open={success}
          TransitionComponent={Zoom}
          keepMounted
          PaperProps={{
            sx: {
              borderRadius: 5,
              p: 2,
              minWidth: { xs: '90%', sm: 400 },
              textAlign: 'center',
              boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            },
          }}
        >
          <DialogContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  bgcolor: '#E8F5E9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <CheckCircle sx={{ fontSize: '3.5rem', color: '#2E7D32' }} />
              </Box>
              <Typography variant='h5' fontWeight='800' color='#1a1a1a'>
                Inscrição Confirmada!
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                Sua vaga foi garantida com sucesso.
              </Typography>
              <Box
                sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <CircularProgress size={16} sx={{ color: '#2E7D32' }} />
                <Typography variant='body2' color='#2E7D32' fontWeight='500'>
                  Redirecionando para seus cursos...
                </Typography>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>

        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Chip
            label='Matrículas Abertas'
            sx={{
              bgcolor: '#E3F2FD',
              color: '#1976D2',
              fontWeight: 'bold',
              mb: 3,
              px: 1,
            }}
          />
          <Typography
            variant='h3'
            component='h1'
            fontWeight='800'
            sx={{
              mb: 2,
              color: '#1a1a1a',
              fontSize: { xs: '2rem', md: '3rem' },
            }}
          >
            {curso.nm_titulo}
          </Typography>
          <Typography
            variant='h6'
            color='text.secondary'
            sx={{
              maxWidth: 600,
              mx: 'auto',
              fontWeight: 'normal',
              lineHeight: 1.6,
            }}
          >
            Selecione a modalidade que melhor se adapta à sua rotina e comece a
            transformar sua carreira hoje mesmo.
          </Typography>
        </Box>

        {/* Modalidade Cards */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            justifyContent: 'center',
            alignItems: 'stretch',
            mb: 10,
          }}
        >
          {/* Remote Card */}
          <Box sx={{ flex: 1, maxWidth: { md: 500 } }}>
            <Paper
              elevation={selectedModalidade === 'R' ? 8 : 0}
              onClick={() => remoteVersion && selectModalidade(remoteVersion)}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 5,
                height: '100%',
                cursor: remoteVersion ? 'pointer' : 'default',
                bgcolor: '#fff',
                border: '2px solid',
                borderColor:
                  selectedModalidade === 'R' ? '#00BCD4' : 'transparent',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                opacity: remoteVersion ? 1 : 0.8,
                filter: remoteVersion ? 'none' : 'grayscale(100%)',
                '&:hover': remoteVersion
                  ? {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.05)',
                      borderColor:
                        selectedModalidade === 'R' ? '#00BCD4' : '#E0E0E0',
                    }
                  : {},
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '20px',
                  bgcolor: '#E0F7FA',
                  color: '#00BCD4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <Devices sx={{ fontSize: '2rem' }} />
              </Box>

              <Typography
                variant='h5'
                fontWeight='800'
                sx={{ mb: 1, color: '#1a1a1a' }}
              >
                Modalidade Remota
              </Typography>

              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 3, minHeight: 48 }}
              >
                Estude onde e quando quiser com acesso vitalício ao conteúdo e
                suporte online.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={<Wifi sx={{ fontSize: '1rem !important' }} />}
                  label='100% Online'
                  size='small'
                  variant='outlined'
                  sx={{ borderRadius: 2 }}
                />
                <Chip
                  icon={<CheckCircle sx={{ fontSize: '1rem !important' }} />}
                  label='Certificado'
                  size='small'
                  variant='outlined'
                  sx={{ borderRadius: 2 }}
                />
                {!remoteVersion && (
                  <Chip
                    label='Indisponível'
                    size='small'
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#F5F5F5',
                      color: '#9E9E9E',
                      fontWeight: 'bold',
                      border: '1px solid #E0E0E0',
                    }}
                  />
                )}
              </Box>

              {selectedModalidade === 'R' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 24,
                    right: 24,
                    color: '#00BCD4',
                  }}
                >
                  <CheckCircle sx={{ fontSize: '2rem' }} />
                </Box>
              )}
            </Paper>
          </Box>

          {/* Hybrid Card */}
          <Box sx={{ flex: 1, maxWidth: { md: 500 } }}>
            <Paper
              elevation={selectedModalidade === 'H' ? 8 : 0}
              onClick={() => hybridVersion && selectModalidade(hybridVersion)}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 5,
                height: '100%',
                cursor: hybridVersion ? 'pointer' : 'default',
                bgcolor: '#fff',
                border: '2px solid',
                borderColor:
                  selectedModalidade === 'H' ? '#FFB74D' : 'transparent',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden',
                opacity: hybridVersion ? 1 : 0.8,
                filter: hybridVersion ? 'none' : 'grayscale(100%)',
                '&:hover': hybridVersion
                  ? {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.05)',
                      borderColor:
                        selectedModalidade === 'H' ? '#FFB74D' : '#E0E0E0',
                    }
                  : {},
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '20px',
                  bgcolor: '#FFF3E0',
                  color: '#FFB74D',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                }}
              >
                <CastForEducation sx={{ fontSize: '2rem' }} />
              </Box>

              <Typography
                variant='h5'
                fontWeight='800'
                sx={{ mb: 1, color: '#1a1a1a' }}
              >
                Modalidade Híbrida
              </Typography>

              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mb: 3, minHeight: 48 }}
              >
                Une a flexibilidade do online com a prática presencial e
                networking exclusivo.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={<Groups sx={{ fontSize: '1rem !important' }} />}
                  label='Aulas Presenciais'
                  size='small'
                  variant='outlined'
                  sx={{ borderRadius: 2 }}
                />
                <Chip
                  icon={<Psychology sx={{ fontSize: '1rem !important' }} />}
                  label='Mentoria'
                  size='small'
                  variant='outlined'
                  sx={{ borderRadius: 2 }}
                />
                {!hybridVersion && (
                  <Chip
                    label='Em Breve'
                    size='small'
                    sx={{
                      borderRadius: 2,
                      bgcolor: '#FFF3E0',
                      color: '#FFB74D',
                      fontWeight: 'bold',
                      border: '1px solid #FFE0B2',
                    }}
                  />
                )}
              </Box>

              {selectedModalidade === 'H' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 24,
                    right: 24,
                    color: '#FFB74D',
                  }}
                >
                  <CheckCircle sx={{ fontSize: '2rem' }} />
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Container>

      {/* Footer Action Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          borderTop: '1px solid #E0E4E7',
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Container maxWidth='lg'>
          <Grid
            container
            spacing={3}
            alignItems='center'
            justifyContent='space-between'
          >
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {/* Modality Selector (Syncs with Cards) */}
                <FormControl
                  size='small'
                  sx={{ minWidth: 200 }}
                  variant='outlined'
                >
                  <InputLabel
                    id='label-modalidade'
                    sx={{ bgcolor: '#fff', px: 0.5 }}
                  >
                    Modalidade
                  </InputLabel>
                  <Select
                    labelId='label-modalidade'
                    label='Modalidade'
                    value={selectedVersionId}
                    onChange={e => {
                      const v = curso.versoes.find(
                        ver => ver.id_curso_versao === e.target.value
                      );
                      selectModalidade(v);
                    }}
                    sx={{ borderRadius: 3, bgcolor: '#fff' }}
                  >
                    {remoteVersion && (
                      <MenuItem value={remoteVersion.id_curso_versao}>
                        Remota
                      </MenuItem>
                    )}
                    {hybridVersion && (
                      <MenuItem value={hybridVersion.id_curso_versao}>
                        Híbrida
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>

                {/* Turma Selector (Conditional) */}
                <Fade
                  in={selectedModalidade === 'H' || selectedModalidade === 'R'}
                  unmountOnExit
                >
                  <FormControl
                    size='small'
                    sx={{ minWidth: 200 }}
                    variant='outlined'
                  >
                    <InputLabel
                      id='label-turma'
                      sx={{ bgcolor: '#fff', px: 0.5 }}
                    >
                      Turma
                    </InputLabel>
                    <Select
                      labelId='label-turma'
                      label='Turma'
                      value={selectedTurma}
                      onChange={e => setSelectedTurma(e.target.value)}
                      sx={{ borderRadius: 3, bgcolor: '#fff' }}
                    >
                      {loadingTurmas ? (
                        <MenuItem value='' disabled>
                          Carregando turmas...
                        </MenuItem>
                      ) : (
                        turmas.map(t => (
                          <MenuItem key={t.id_turma} value={t.id_turma}>
                            {t.nm_descricao}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Fade>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Button
                variant='contained'
                size='large'
                onClick={handleRegister}
                disabled={
                  registering ||
                  !selectedVersionId ||
                  ((selectedModalidade === 'H' || selectedModalidade === 'R') &&
                    !selectedTurma)
                }
                endIcon={
                  registering ? (
                    <CircularProgress size={20} color='inherit' />
                  ) : (
                    <ArrowForward />
                  )
                }
                sx={{
                  bgcolor: '#1a1a1a',
                  color: '#fff',
                  fontWeight: 'bold',
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                  '&:hover': {
                    bgcolor: '#000',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 20px rgba(0,0,0,0.15)',
                  },
                  transition: 'all 0.2s',
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                {registering ? 'Processando...' : 'Confirmar Inscrição'}
              </Button>
            </Grid>
          </Grid>
          {error && (
            <Alert
              severity='error'
              sx={{ mt: 2, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
        </Container>
      </Paper>
    </Box>
  );
};

export default InscricaoCursoPage;
